import { IDevice } from "lavva.exalushome/build/js/Services/Devices/IDevice";
import { BlindConfigService, BlindConfigurationErrorCode } from "../../../Common/BlindConfigurations/BlindConfigService";
import { BlindWorkStats, ISsr_4_0_0_0, WorkMode } from "./ISsrVersionedService";
import { BlindCalibrationState, BlindCalibrationStatusEnum, StateReliability } from "lavva.exalushome/build/js/Services/Devices/IDeviceState";
import { ResponseResult } from "lavva.exalushome/build/js/Services/FieldChangeResult";
import { DataFrame, Method, Status } from "lavva.exalushome/build/js/DataFrame";
import { BlindActionEnum, SetBlindPosition } from "lavva.exalushome/build/js/Services/Devices/Tasks/Tasks";
import { DeviceTaskExecutionResult } from "lavva.exalushome/build/js/Services/Devices/TaskExecutionResult";
import { CorrectionSteps, CorrectionType } from "../../../Common/BlindConfigurations/BlindConfigParameters";
import { IUsersService } from "lavva.exalushome/build/js/Services/Users/IUsersService";
import { UsersService } from "lavva.exalushome/build/js/Services/Users/UsersService";
import { Api } from "lavva.exalushome";
import { AccessLevel } from "lavva.exalushome/build/js/Services/Users/IUser";
import { IUpdatesProvider } from "lavva.exalushome/build/js/Services/Updates/IUpdatesProvider";
import { UpdatesProvider } from "lavva.exalushome/build/js/Services/Updates/UpdatesProvider";

export class Ssr_4_0_0_0 extends BlindConfigService implements ISsr_4_0_0_0 {

    constructor() {
        super();
    }

    override async RunAutomaticCalibrationAsync(device: IDevice, channel: number): Promise<BlindCalibrationState | ResponseResult<BlindConfigurationErrorCode>> {
        try {

            if (!device.Channels.any(ch => ch.Number === channel)) {
                return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.InvalidChannelNumber, `Cannot calibrate blind - invalid channel number: ${channel}`);
            }

            const task = new SetBlindPosition();
            task.Action = BlindActionEnum.Stop;

            switch (await device.Channels.first(ch => ch.Number == channel).ExecuteTaskAsync(task)) {
                case DeviceTaskExecutionResult.Executed:
                    break;
                default:
                    return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.DeviceNotResponding, `Cannot calibrate blind - device is not responding.`);
            }

            const calibrationReq = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new EnableAutoCalibraionRequest(device.Guid), 8000, false);

            if (calibrationReq == null || calibrationReq.Status == null)
                return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.OtherError, `Cannot calibrate blind - calibration request failed for an unknown reason.`);

            switch (calibrationReq.Status) {
                case Status.OK:
                    {
                        var r = new BlindCalibrationState();
                        r.CalibrationStatus = BlindCalibrationStatusEnum.AutoLernInProgress;
                        r.Channel = channel;
                        r.StateReliability = StateReliability.Confident

                        return r;
                    }
                case Status.Error:
                    return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.CalibrationsIsCurrentlyRunning, `Cannot calibrate blind - calibration is currently running.`);
                default:
                    return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.OtherError, `Cannot calibrate blind - calibration request failed - request status code: ${calibrationReq.Status}`);

            }
        }
        catch (error) {
            return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.OtherError, `Cannot calibrate blind - fatal error: ${error}`);
        }
    }

    override async GetCorrectionStepAsync(device: IDevice, channel: number, correctionType: CorrectionType): Promise<CorrectionSteps | ResponseResult<BlindConfigurationErrorCode>> {
        try {
            const version = await Api.Get<IUpdatesProvider>(UpdatesProvider.ServiceName).GetSoftwareRuntimeInfoAsync();
            const [major, minor] = version.SoftwareVersion.split(".").map(v => parseInt(v));

            if (major >= 7 && minor >= 45) {
                const workLog = await this.GetWorkLogAsyncRaw(device, channel);
                if (workLog instanceof ResponseResult)
                    return workLog;

                //if true - manual, false - auto
                if (workLog.ManualAutoConfigFlag && correctionType === "UPPER") {
                    return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.NotAvailableInManualMode, `Cannot get correction step - not available in manual mode.`);
                }

                return super.GetCorrectionStepAsync(device, channel, correctionType);
            }
            else
                return super.GetCorrectionStepAsync(device, channel, correctionType);
        }
        catch (error) {
            return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.OtherError, `Cannot get correction step - fatal error: ${error}`);
        }
    }

    override async SetCorrectionStepAsync(device: IDevice, channel: number, correction: number, correctionType: CorrectionType): Promise<Status> {
        try {
            const version = await Api.Get<IUpdatesProvider>(UpdatesProvider.ServiceName).GetSoftwareRuntimeInfoAsync();
            const [major, minor] = version.SoftwareVersion.split(".").map(v => parseInt(v));

            if (major >= 7 && minor >= 45) {
                const workLog = await this.GetWorkLogAsyncRaw(device, channel);
                if (workLog instanceof ResponseResult) {
                    this._logger.Error(`Cannot set correction step - cannot get work log - ${workLog.Type} - ${workLog.Data}`);
                    return Status.Error;
                }

                //if true - manual, false - auto
                if (workLog.ManualAutoConfigFlag && correctionType === "UPPER") {
                    this._logger.Warning(`Cannot set correction step - not available in manual mode.`);
                    return Status.WrongData;
                }

                return await super.SetCorrectionStepAsync(device, channel, correction, correctionType);
            } else
                return await super.SetCorrectionStepAsync(device, channel, correction, correctionType);
        }
        catch (error) {
            this._logger.Error(`Cannot set correction step - fatal error: ${error}`);
            return Status.FatalError;
        }
    }

    async GetHeadSensorStatusAsync(device: IDevice, channel: number): Promise<boolean | ResponseResult<BlindConfigurationErrorCode>> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.InvalidChannelNumber, `Cannot get head sensor status - invalid channel number: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<boolean>(new GetHeadSensorStatusRequest(device.Guid, channel), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.OtherError, `Cannot get head sensor status - request failed for an unknown reason.`);

            if (result.Status != Status.OK)
                return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.OtherError, `Cannot get head sensor status - request failed - request status code: ${result.Status}`);

            if (result.Data == null)
                return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.NoData, `Cannot get head sensor status - request failed - no data received.`);

            return result.Data;
        }
        catch (error) {
            return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.OtherError, `Cannot get head sensor status - fatal error: ${error}`);
        }
    }

    async SwitchHeadSensorStatusAsync(enabled: boolean, device: IDevice, channel: number): Promise<Status> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return Status.WrongData;

            const result = await this._connection?.SendAndWaitForResponseAsync<boolean>(new SwitchHeadSensorStatusRequest(device.Guid, channel, enabled), 8000, false);

            if (result == null || result.Status == null)
                return Status.FatalError;

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }

    async ChangeMovementDirectionAsync(device: IDevice, channel: number): Promise<Status> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return Status.WrongData;

            const result = await this._connection?.SendAndWaitForResponseAsync<boolean>(new ChangeMovementDirectionRequest(device.Guid, channel), 8000, false);

            if (result == null || result.Status == null)
                return Status.FatalError;

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }

    async GetWorkingModeAsync(device: IDevice, channel: number): Promise<WorkMode | ResponseResult<BlindConfigurationErrorCode>> {
        const version = await Api.Get<IUpdatesProvider>(UpdatesProvider.ServiceName).GetSoftwareRuntimeInfoAsync();
        const [major, minor] = version.SoftwareVersion.split(".").map(v => parseInt(v));

        if (major >= 7 && minor >= 45) {
            const workLog = await this.GetWorkLogAsyncRaw(device, channel);
            if (workLog instanceof ResponseResult) {
                return workLog;
            }

            //true - manual, false - auto
            if (workLog.ManualAutoConfigFlag) {
                return WorkMode.Manual;
            } else {
                return WorkMode.Automatic;
            }
        }
        else 
            return WorkMode.Automatic;
    }

    async GetWorkLogAsync(device: IDevice, channel: number): Promise<BlindWorkStats | ResponseResult<BlindConfigurationErrorCode>> {
        try {
            const user = await Api.Get<IUsersService>(UsersService.ServiceName).GetUserAsync();

            if (user == null || user.AccessLevel < AccessLevel.Support)
                return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.InsufficientPermissions, `Cannot get head sensor status - current user has insufficient permissions.`);

            return await this.GetWorkLogAsyncRaw(device, channel);

        } catch (error) {
            return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.OtherError, `Cannot get work log - fatal error: ${error}`);
        }
    }

    private async GetWorkLogAsyncRaw(device: IDevice, channel: number): Promise<BlindWorkStats | ResponseResult<BlindConfigurationErrorCode>> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.InvalidChannelNumber, `Cannot get head sensor status - invalid channel number: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<BlindWorkStats>(new GetBlindWorkStats(device.Guid, channel), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.OtherError, `Cannot get work log - request failed for an unknown reason.`);

            if (result.Status != Status.OK)
                return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.OtherError, `Cannot get work log - request failed - request status code: ${result.Status}`);

            if (result.Data == null)
                return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.NoData, `Cannot get work log - request failed - no data received.`);

            return result.Data;

        } catch (error) {
            return new ResponseResult<BlindConfigurationErrorCode>(BlindConfigurationErrorCode.OtherError, `Cannot get work log - fatal error: ${error}`);
        }
    }
}

//Auto calibration
class EnableAutoCalibraionRequest extends DataFrame<string> {
    constructor(guid: string) {
        super();
        this.Resource = "/extafree2/blind/calibration/auto";
        this.Method = Method.Get;
        this.Data = guid;
    }
}

class GetHeadSensorStatusRequest extends DataFrame<any> {
    constructor(guid: string, channel: number) {
        super();
        this.Resource = "/extalife/device/blind/head/sensor/status";
        this.Method = Method.Get;
        this.Data = {
            DeviceGuid: guid,
            Channel: channel
        };
    }
}

class SwitchHeadSensorStatusRequest extends DataFrame<any> {
    constructor(guid: string, channel: number, enabled: boolean) {
        super();
        this.Resource = "/extalife/device/blind/head/sensor/switch";
        this.Method = Method.Put;
        this.Data = {
            DeviceGuid: guid,
            Channel: channel,
            Enable: enabled
        };
    }
}

class ChangeMovementDirectionRequest extends DataFrame<any> {
    constructor(guid: string, channel: number) {
        super();
        this.Resource = "/extalife/device/blind/direction/reverse";
        this.Method = Method.Post;
        this.Data = {
            DeviceGuid: guid,
            Channel: channel
        };
    }
}

class GetBlindWorkStats extends DataFrame<any> {
    constructor(guid: string, channel: number) {
        super();
        this.Resource = "/extalife/device/blind/work/log";
        this.Method = Method.Get;
        this.Data = {
            DeviceGuid: guid,
            Channel: channel
        };
    }
}